import { useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Button, Divider, Modal, Space, Spin, Tag, Typography, message } from 'antd';
import { DownloadOutlined, FilePdfOutlined, SaveOutlined, SendOutlined } from '@ant-design/icons';
import { Document, Page, pdfjs } from 'react-pdf';
import { PDFCheckBox, PDFDocument, PDFRadioGroup, PDFTextField } from 'pdf-lib';
import type { Resource, User, WorksheetAttempt } from './types';
import { api } from './api';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';

pdfjs.GlobalWorkerOptions.workerSrc = new URL('pdfjs-dist/build/pdf.worker.min.mjs', import.meta.url).toString();

interface Props {
  open: boolean;
  resource: Resource;
  user: User;
  onClose: () => void;
  onChanged: () => void;
}

type FormState = Record<string, string | boolean>;

function fileName(title: string) {
  return `${title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '') || 'worksheet'}-completed.pdf`;
}

export default function PdfWorksheet({ open, resource, user, onClose, onChanged }: Props) {
  const attempt = resource.attempt;
  const editable = !attempt || attempt.status === 'draft';
  const [pages, setPages] = useState(0);
  const [ready, setReady] = useState(false);
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [fieldNames, setFieldNames] = useState<Record<string, string>>({});
  const [values, setValues] = useState<FormState>(attempt?.fieldState || {});
  const viewer = useRef<HTMLDivElement>(null);
  const saveTimer = useRef<number | undefined>(undefined);

  useEffect(() => {
    setPages(0); setReady(false); setFieldNames({}); setValues(attempt?.fieldState || {});
  }, [resource.id, attempt?.id, open]);

  useEffect(() => {
    if (!ready || !editable) return;
    window.clearTimeout(saveTimer.current);
    saveTimer.current = window.setTimeout(async () => {
      try {
        setSaving(true);
        await api.post<WorksheetAttempt>(`/api/v1/worksheets/${resource.id}/draft`, { fieldState: values });
      } catch (error) {
        message.error(error instanceof Error ? error.message : 'Draft could not be saved.');
      } finally { setSaving(false); }
    }, 650);
    return () => window.clearTimeout(saveTimer.current);
  }, [editable, ready, resource.id, values]);

  const pdfFile = useMemo(() => resource.downloadUrl ? { url: resource.downloadUrl, withCredentials: true } : null, [resource.downloadUrl]);

  async function documentReady(pdf: any) {
    setPages(pdf.numPages);
    const objectMap = await pdf.getFieldObjects?.();
    const names: Record<string, string> = {};
    Object.entries(objectMap || {}).forEach(([name, entries]) => {
      (entries as Array<{ id?: string }>).forEach((entry) => { if (entry.id) names[entry.id] = name; });
    });
    const initial: FormState = { ...(attempt?.fieldState || {}) };
    Object.values(names).forEach((name) => {
      if (initial[name] !== undefined) return;
      if (/^(name|student[ _-]?name)$/i.test(name)) initial[name] = user.displayName;
      if (/^date$/i.test(name)) initial[name] = new Date().toLocaleDateString();
    });
    setFieldNames(names); setValues(initial); setReady(true);
  }

  useEffect(() => {
    if (!ready) return;
    const timers = [0, 120, 450].map((delay) => window.setTimeout(hydrateFields, delay));
    return () => timers.forEach((timer) => window.clearTimeout(timer));
  }, [fieldNames, ready, values]);

  function hydrateFields() {
    if (!viewer.current) return;
    const inferred: FormState = {};
    viewer.current.querySelectorAll<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>('[data-element-id]').forEach((element) => {
      const id = element.dataset.elementId || '';
      const field = fieldNames[id] || element.getAttribute('name') || id;
      let value = values[field];
      if (value === undefined && /^(name|student[ _-]?name)$/i.test(field)) { value = user.displayName; inferred[field] = value; }
      if (value === undefined && /^date$/i.test(field)) { value = new Date().toLocaleDateString(); inferred[field] = value; }
      if (value === undefined) return;
      if (element instanceof HTMLInputElement && element.type === 'checkbox') element.checked = Boolean(value);
      else if ('value' in element) element.value = String(value);
    });
    if (Object.keys(inferred).length) setValues((previous) => ({ ...inferred, ...previous }));
  }

  function recordInput(event: React.FormEvent<HTMLDivElement>) {
    if (!editable) return;
    const target = event.target as HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement;
    const id = target.dataset?.elementId;
    if (!id) return;
    const field = fieldNames[id] || target.getAttribute('name') || id;
    const value = target instanceof HTMLInputElement && target.type === 'checkbox' ? target.checked : target.value;
    setValues((previous) => ({ ...previous, [field]: value }));
  }

  async function filledPdf(): Promise<Blob> {
    if (!resource.downloadUrl) throw new Error('The worksheet file is missing.');
    const source = await api.blob(resource.downloadUrl);
    const document = await PDFDocument.load(await source.arrayBuffer());
    const form = document.getForm();
    form.getFields().forEach((field) => {
      const value = values[field.getName()];
      if (value === undefined || /grader|note.*grader/i.test(field.getName())) return;
      if (field instanceof PDFTextField) field.setText(String(value));
      if (field instanceof PDFCheckBox) value ? field.check() : field.uncheck();
      if (field instanceof PDFRadioGroup && typeof value === 'string') {
        try { field.select(value); } catch { /* Preserve a form option mismatch. */ }
      }
    });
    form.updateFieldAppearances();
    const saved = await document.save();
    const output = new Uint8Array(saved.byteLength);
    output.set(saved);
    return new Blob([output.buffer], { type: 'application/pdf' });
  }

  async function submitFilled() {
    try {
      setSubmitting(true);
      const completed = await filledPdf();
      const data = new FormData();
      data.append('upload', new File([completed], fileName(resource.title), { type: 'application/pdf' }));
      await api.form(`/api/v1/worksheets/${resource.id}/submit`, data);
      message.success('Worksheet submitted. It will count as complete when your grader leaves a note.');
      onChanged();
    } catch (error) {
      message.error(error instanceof Error ? error.message : 'Worksheet could not be submitted.');
    } finally { setSubmitting(false); }
  }

  async function submitReplacement(event: React.ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.pdf')) { message.error('Please choose a PDF.'); return; }
    try {
      setSubmitting(true);
      const data = new FormData(); data.append('upload', file);
      await api.form(`/api/v1/worksheets/${resource.id}/submit`, data);
      message.success('Replacement worksheet submitted.'); onChanged();
    } catch (error) { message.error(error instanceof Error ? error.message : 'Worksheet could not be submitted.'); }
    finally { setSubmitting(false); event.target.value = ''; }
  }

  const reviewed = attempt?.status === 'reviewed';
  return <Modal open={open} onCancel={onClose} width="min(1180px, calc(100vw - 28px))" footer={null} className="worksheet-modal" title={<Space><FilePdfOutlined /><span>{resource.title}</span>{attempt?.late ? <Tag color="orange">Submitted late</Tag> : null}</Space>}>
    {reviewed ? <Alert type="success" showIcon message="Your grader left a note" description={<><Typography.Paragraph>{attempt.note}</Typography.Paragraph>{attempt.returnedUrl ? <Button icon={<DownloadOutlined />} href={attempt.returnedUrl} target="_blank">Open returned PDF</Button> : null}</>} /> : null}
    {attempt?.status === 'submitted' ? <Alert type="info" showIcon message="Submitted — awaiting a grader note" description="Your work is now read-only. You will receive an email when a grader leaves a note." /> : null}
    {editable ? <div className="worksheet-toolbar"><div><Typography.Text type="secondary">{saving ? 'Saving draft…' : ready ? 'Draft saved automatically' : 'Loading worksheet…'}</Typography.Text></div><Space wrap><Button icon={<DownloadOutlined />} href={resource.downloadUrl || undefined} target="_blank">Download original</Button><label className="ant-btn"><input hidden type="file" accept="application/pdf" onChange={submitReplacement} />Replace with PDF</label><Button type="primary" icon={<SendOutlined />} loading={submitting} onClick={submitFilled}>Submit completed PDF</Button></Space></div> : <Divider />}
    <div className="pdf-viewer" ref={viewer} onInput={recordInput} onChange={recordInput} aria-busy={!ready}>
      {pdfFile ? <Document file={pdfFile} onLoadSuccess={documentReady} loading={<div className="pdf-loading"><Spin size="large" /><span>Preparing your worksheet…</span></div>} error={<Alert type="error" message="This worksheet could not be opened." />}>
        {Array.from({ length: pages }, (_, index) => <Page key={index} pageNumber={index + 1} width={760} renderTextLayer renderAnnotationLayer renderForms onRenderAnnotationLayerSuccess={hydrateFields} />)}
      </Document> : <Alert type="error" message="Worksheet file is unavailable." />}
    </div>
    {editable ? <Typography.Paragraph type="secondary" className="worksheet-help"><SaveOutlined /> Type directly into the PDF. Your draft saves automatically; use the replacement option if you completed the PDF in another application.</Typography.Paragraph> : null}
  </Modal>;
}
